import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { UserContext } from './UserContext';

const UsageFiltersContext = createContext(null);

export function UsageFiltersProvider({ children }) {
  const user = useContext(UserContext);
  const uid = useMemo(() => (
    user?.id || user?.user_id || user?.email || user?.username || 'anon'
  ), [user]);

  // storage keys (per user)
  const keyRange = `owly:usage:dateRange:${uid}`;
  const keyAgent = `owly:usage:selectedAgent:${uid}`;
  const keyViewType = `owly:usage:viewType:${uid}`;
  const keySecondaryView = `owly:usage:secondaryView:${uid}`;
  const keySecondaryMode = `owly:usage:secondaryMode:${uid}`;

  // date range
  const [dateRange, setDateRange] = useState(() => {
    try {
      const v = typeof window !== 'undefined' ? localStorage.getItem(keyRange) : null;
      return v || '7';
    } catch (_) {
      return '7';
    }
  });

  // agent
  const [selectedAgent, setSelectedAgent] = useState(() => {
    try {
      const v = typeof window !== 'undefined' ? localStorage.getItem(keyAgent) : null;
      return v || 'all';
    } catch (_) {
      return 'all';
    }
  });

  // primary chart view type (credits|users|messages)
  const [viewType, setViewType] = useState(() => {
    try {
      const v = typeof window !== 'undefined' ? localStorage.getItem(keyViewType) : null;
      return v || 'credits';
    } catch (_) {
      return 'credits';
    }
  });

  // secondary chart view (credits|users|messages)
  const [secondaryView, setSecondaryView] = useState(() => {
    try {
      const v = typeof window !== 'undefined' ? localStorage.getItem(keySecondaryView) : null;
      return v || 'users';
    } catch (_) {
      return 'users';
    }
  });

  // secondary chart mode (trend|movavg7|cumulative|dod)
  const [secondaryMode, setSecondaryMode] = useState(() => {
    try {
      const v = typeof window !== 'undefined' ? localStorage.getItem(keySecondaryMode) : null;
      return v || 'trend';
    } catch (_) {
      return 'trend';
    }
  });

  // persist
  useEffect(() => {
    try { if (typeof window !== 'undefined') localStorage.setItem(keyRange, String(dateRange)); } catch (_) {}
  }, [keyRange, dateRange]);

  useEffect(() => {
    try { if (typeof window !== 'undefined') localStorage.setItem(keyAgent, String(selectedAgent)); } catch (_) {}
  }, [keyAgent, selectedAgent]);

  useEffect(() => {
    try { if (typeof window !== 'undefined') localStorage.setItem(keyViewType, String(viewType)); } catch (_) {}
  }, [keyViewType, viewType]);

  useEffect(() => {
    try { if (typeof window !== 'undefined') localStorage.setItem(keySecondaryView, String(secondaryView)); } catch (_) {}
  }, [keySecondaryView, secondaryView]);

  useEffect(() => {
    try { if (typeof window !== 'undefined') localStorage.setItem(keySecondaryMode, String(secondaryMode)); } catch (_) {}
  }, [keySecondaryMode, secondaryMode]);

  const value = useMemo(() => ({
    dateRange,
    setDateRange,
    selectedAgent,
    setSelectedAgent,
    viewType,
    setViewType,
    secondaryView,
    setSecondaryView,
    secondaryMode,
    setSecondaryMode,
  }), [dateRange, selectedAgent, viewType, secondaryView, secondaryMode]);

  return (
    <UsageFiltersContext.Provider value={value}>
      {children}
    </UsageFiltersContext.Provider>
  );
}

export function useUsageFilters() {
  const ctx = useContext(UsageFiltersContext);
  if (!ctx) throw new Error('useUsageFilters must be used within UsageFiltersProvider');
  return ctx;
}
